/**
 * Pure iso geometry for the 2:1 dimetric viewport (RENDERING.md §Iso
 * geometry contract). No DOM/canvas — unit-testable in node and reused by the
 * M2 sprite lab (a flat 10×10 plane with no walls). The base tile is the
 * fixed 96×48 contract; every function takes the unit scale `u` and the
 * camera offset (camX, camY in css px) explicitly so the caller owns state.
 *
 * World→screen (canvas css px; the viewport shows map-space [camX, camX+viewW]
 * × [camY, camY+viewH]):
 *   sx = (x − y)·48·u − camX
 *   sy = (x + y)·24·u − camY
 *
 * Inverse (input mapping): a = (sx+camX)/(48u), b = (sy+camY)/(24u),
 *   x = floor((a+b)/2), y = floor((b−a)/2).
 * Clicks in the iso dead zones (outside the map diamond) floor to
 * out-of-bounds tile coords deliberately — actions.ts already maps those to
 * walk-to-nearest-revealed-floor and primaryAction treats them as no-ops; the
 * client only picks intentions (AGENTS §6).
 */

/** Base tile full dims (fixed contract). */
export const TILE_W = 96;
export const TILE_H = 48;
/** Per-tile-step projection offsets (half-dims). */
export const HALF_W = TILE_W / 2;
export const HALF_H = TILE_H / 2;

export interface IsoPoint {
  x: number;
  y: number;
}

export function tileToScreen(tx: number, ty: number, u: number, camX: number, camY: number): IsoPoint {
  return {
    x: (tx - ty) * HALF_W * u - camX,
    y: (tx + ty) * HALF_H * u - camY,
  };
}

export function screenToTile(sx: number, sy: number, u: number, camX: number, camY: number): IsoPoint {
  const a = (sx + camX) / (HALF_W * u);
  const b = (sy + camY) / (HALF_H * u);
  return { x: Math.floor((a + b) / 2), y: Math.floor((b - a) / 2) };
}

export interface Camera {
  camX: number;
  camY: number;
}

/**
 * Exponential camera approach toward `desired` (the lab's follow feel):
 * `k = 1 − e^(−dt·perS)` per axis, so the camera covers ~63% of the gap per
 * `1/perS` seconds, never overshoots, and settles asymptotically. `dt` is
 * clamped to 100 ms so a rAF hitch can't cause a teleport. Zero dt → no
 * move; `ready = false` snaps directly to `desired` (first frame, or after a
 * layout change). Pure — the caller owns the smoothed Camera state.
 */
/**
 * Snap a camera offset to whole DEVICE pixels (css px rounded at `dpr`).
 *
 * The whole world is drawn through one `translate(-camX, -camY)`, so a camera
 * sitting on a fractional device pixel resamples every sprite on screen at a
 * fresh sub-pixel phase EVERY FRAME — while the camera glides, the static world
 * boils (worst on the sprite pack's hard 1-px outlines) and text/faces never
 * hold still. Quantising the camera costs ≤ 0.5 device px of positional error
 * (the standard pixel-art camera trade, and smaller than one art texel) and buys
 * a world that stops shimmering.
 *
 * The CAMERA is snapped rather than the zoom (`u`): snapping the unit to an
 * integer would also make the art sample 1:1, but at this window it re-picks the
 * framing — a fit-to-window zoom change the player sees immediately.
 */
export function snapCamera(cam: Camera, dpr: number): Camera {
  const q = dpr > 0 ? dpr : 1;
  return { camX: Math.round(cam.camX * q) / q, camY: Math.round(cam.camY * q) / q };
}

export function cameraLerp(
  prev: Camera,
  desired: Camera,
  dtMs: number,
  perS: number,
  ready: boolean,
): Camera {
  if (!ready) return desired;
  if (dtMs <= 0) return prev;
  const k = 1 - Math.exp((-Math.min(dtMs, 100) / 1000) * perS);
  return {
    camX: prev.camX + (desired.camX - prev.camX) * k,
    camY: prev.camY + (desired.camY - prev.camY) * k,
  };
}

/**
 * Player-centered camera clamped so the map's diamond bounding box stays at
 * least PAD inside the viewport (the map can be wider than the view — the
 * clamp only stops the camera from panning past the padded map edges, so at a
 * map edge the player is never glued to the screen edge). When the padded
 * diamond fits the viewport entirely (small maps — e.g. M2's 10×10 lab plane)
 * the diamond is centered instead.
 *
 * Diamond bbox (map space, camX = camY = 0):
 *   left   tile (0, h−1)      → −(h−1)·48u
 *   right  tile (w−1, 0)      →  (w−1)·48u
 *   top    tile (0, 0)        →  0
 *   bottom tile (w−1, h−1)    →  (w+h−2)·24u
 * PAD = 2 tile-widths horizontal, 2 tile-heights vertical.
 */
export function cameraClamp(
  me: IsoPoint,
  w: number,
  h: number,
  u: number,
  viewW: number,
  viewH: number,
): Camera {
  const padX = 2 * TILE_W * u;
  const padY = 2 * TILE_H * u;
  const bMinX = -(h - 1) * HALF_W * u;
  const bMaxX = (w - 1) * HALF_W * u;
  const bMinY = 0;
  const bMaxY = (w + h - 2) * HALF_H * u;

  const target = tileToScreen(me.x, me.y, u, 0, 0);
  let camX = target.x - viewW / 2;
  let camY = target.y - viewH / 2;

  // camX bounds: the viewport [camX, camX+viewW] must contain [bMinX−padX,
  // bMaxX+padX]; when that's impossible (padded map narrower than the view),
  // center the diamond instead.
  const minCamX = bMinX - padX;
  const maxCamX = bMaxX + padX - viewW;
  if (minCamX > maxCamX) camX = (bMinX + bMaxX) / 2 - viewW / 2;
  else camX = Math.max(minCamX, Math.min(camX, maxCamX));

  const minCamY = bMinY - padY;
  const maxCamY = bMaxY + padY - viewH;
  if (minCamY > maxCamY) camY = (bMinY + bMaxY) / 2 - viewH / 2;
  else camY = Math.max(minCamY, Math.min(camY, maxCamY));

  return { camX, camY };
}

/**
 * Exact visibility predicate: the tile's diamond bbox (sx ∈ [sx0−48u, sx0+48u],
 * sy ∈ [sy0, sy0+48u] where (sx0, sy0) is the tile anchor) intersects the
 * viewport rect.
 */
export function tileInView(
  tx: number,
  ty: number,
  u: number,
  camX: number,
  camY: number,
  viewW: number,
  viewH: number,
): boolean {
  const sx0 = (tx - ty) * HALF_W * u - camX;
  const sy0 = (tx + ty) * HALF_H * u - camY;
  return (
    sx0 + HALF_W * u >= 0 &&
    sx0 - HALF_W * u <= viewW &&
    sy0 + TILE_H * u >= 0 &&
    sy0 <= viewH
  );
}

/**
 * The tight bounding box of the visible tile set — the tiles whose diamond
 * bboxes intersect the camera rect, iterated via the (a = x−y, b = x+y)
 * parallelogram (parity-filtered to integer tiles, clipped to the map) so a
 * ~20-tile viewport over a 43×43 map visits only its slice, never all 1849.
 * Null when nothing is on screen. Callers iterate the box and re-apply
 * `tileInView` to skip the corner tiles the box must over-approximate.
 */
export function visibleTileRange(
  w: number,
  h: number,
  u: number,
  camX: number,
  camY: number,
  viewW: number,
  viewH: number,
): { x0: number; y0: number; x1: number; y1: number } | null {
  const aMin = Math.floor(camX / (HALF_W * u) - 1);
  const aMax = Math.ceil((camX + viewW) / (HALF_W * u) + 1);
  const bMin = Math.floor(camY / (HALF_H * u) - 2);
  const bMax = Math.ceil((camY + viewH) / (HALF_H * u) + 1);

  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (let a = aMin; a <= aMax; a++) {
    for (let b = bMin; b <= bMax; b++) {
      if (((a + b) & 1) !== 0) continue; // x, y integers ⟺ a, b same parity
      const x = (a + b) / 2;
      const y = (b - a) / 2;
      if (x < 0 || y < 0 || x >= w || y >= h) continue;
      if (!tileInView(x, y, u, camX, camY, viewW, viewH)) continue;
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0) return null;
  return { x0, y0, x1, y1 };
}

/** Sortable drawable: terrain (floors, walls) or entity (everything standing). */
export type DrawKind = "terrain" | "entity";

export interface Paintable {
  x: number;
  y: number;
  kind: DrawKind;
}

/**
 * Painter's algorithm comparator (RENDERING.md §Painter's algorithm):
 * ascending depth x+y (farther tiles — smaller x+y, higher on screen — draw
 * first, so a wall occludes a character behind it and a character in front
 * stands before it); at equal depth, terrain/walls before entities; then by
 * x; then by y. All ascending, all deterministic.
 */
export function painterOrder(a: Paintable, b: Paintable): number {
  const da = a.x + a.y;
  const db = b.x + b.y;
  if (da !== db) return da - db;
  const ka = a.kind === "terrain" ? 0 : 1;
  const kb = b.kind === "terrain" ? 0 : 1;
  if (ka !== kb) return ka - kb;
  if (a.x !== b.x) return a.x - b.x;
  return a.y - b.y;
}
